import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Avatar } from '../shared/Avatar';
import { Camera, Trash2 } from '../../icons';
import { uploadMyAvatar, deleteMyAvatar } from '../../api/users';
import { cropAvatarToSquarePng } from '../../lib/image-crop';
import { avatarImageUrl } from '../../lib/avatar-src';
import { tSettings, getSettingsLang, subscribeSettingsLang } from '../../lib/settings-i18n';
import './AvatarUploader.css';

/**
 * AvatarUploader (design-20260606 §17.1, 서버 경유) — 프로필 이미지 등록/수정/삭제.
 *
 * 파일 선택 → 512² center-crop(PNG) → PUT /users/me/avatar(서버 대리 저장) → onChange(새 avatarUrl).
 * 삭제 → DELETE /users/me/avatar → onChange(null). 표시는 avatarImageUrl(서버 스트림 URL).
 * (운영 MinIO 가 브라우저 비공개라 presigned 직결 불가 → 서버 경유로 전환.)
 */

const ACCEPT = 'image/png,image/jpeg,image/webp,image/gif';
const ACCEPT_RE = /^image\/(png|jpe?g|webp|gif)$/i;
const MAX_SOURCE_BYTES = 10 * 1024 * 1024;

interface AvatarUploaderProps {
  userId: number;
  name: string;
  value: string | null;
  onChange: (next: string | null) => void;
  disabled?: boolean;
  /**
   * 본인이 사진을 못 바꾸는 자리(2026-09-13 사용자 결정 — 사진은 인사에서 관리자가 고친다).
   * 사진만 보여 주고 변경·삭제 버튼과 파일 입력을 **그리지 않는다**. 기본값 false 라 종전 그대로다.
   */
  readOnly?: boolean;
}

export function AvatarUploader({ userId, name, value, onChange, disabled, readOnly = false }: AvatarUploaderProps) {
  useSyncExternalStore(subscribeSettingsLang, getSettingsLang, getSettingsLang);
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  const displaySrc = localPreview ?? avatarImageUrl(userId, value);
  const hasPhoto = Boolean(value || localPreview);

  useEffect(() => () => { if (localPreview) URL.revokeObjectURL(localPreview); }, [localPreview]);

  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError(null);
    if (!ACCEPT_RE.test(file.type)) { setError(tSettings('settings.profile.photo_err_type')); return; }
    if (file.size > MAX_SOURCE_BYTES) { setError(tSettings('settings.profile.photo_err_size')); return; }
    setBusy(true);
    try {
      const blob = await cropAvatarToSquarePng(file, 512);
      const preview = URL.createObjectURL(blob);
      setLocalPreview((prev) => { if (prev) URL.revokeObjectURL(prev); return preview; });
      const profile = await uploadMyAvatar(blob, 'image/png');
      onChange(profile.avatarUrl ?? null);
    } catch (err) {
      setError(tSettings('settings.profile.photo_err_upload', {
        msg: err instanceof Error ? err.message : String(err),
      }));
    } finally {
      setBusy(false);
    }
  }

  async function onRemove() {
    setError(null);
    setBusy(true);
    try {
      await deleteMyAvatar();
      setLocalPreview((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
      onChange(null);
    } catch (err) {
      setError(tSettings('settings.profile.photo_err_upload', {
        msg: err instanceof Error ? err.message : String(err),
      }));
    } finally {
      setBusy(false);
    }
  }

  if (readOnly) {
    return (
      <div className="avatar-uploader avatar-uploader--readonly">
        <div className="avatar-uploader-ring">
          <Avatar userId={userId} name={name} src={displaySrc} size="xl" />
        </div>
        <p className="avatar-uploader-hint">{tSettings('settings.profile.photo_readonly')}</p>
      </div>
    );
  }

  return (
    <div className="avatar-uploader">
      <div className={`avatar-uploader-ring${busy ? ' is-busy' : ''}`}>
        <button
          type="button"
          className="avatar-uploader-photo"
          onClick={() => inputRef.current?.click()}
          disabled={disabled || busy}
          aria-label={tSettings('settings.profile.photo_change')}
        >
          <Avatar userId={userId} name={name} src={displaySrc} size="xl" />
          <span className="avatar-uploader-overlay" aria-hidden>
            <Camera size={20} />
            <span className="avatar-uploader-overlay-text">{tSettings('settings.profile.photo_change')}</span>
          </span>
          {busy && <span className="avatar-uploader-loading" aria-label={tSettings('settings.profile.photo_uploading')} />}
        </button>
        {hasPhoto && !busy && (
          <button
            type="button"
            className="avatar-uploader-remove"
            onClick={onRemove}
            disabled={disabled}
            aria-label={tSettings('settings.profile.photo_remove')}
            title={tSettings('settings.profile.photo_remove')}
          >
            <Trash2 size={14} />
          </button>
        )}
        <input ref={inputRef} type="file" accept={ACCEPT} onChange={onPick} hidden aria-hidden="true" tabIndex={-1} />
      </div>
      <p className="avatar-uploader-hint">{tSettings('settings.profile.photo_hint')}</p>
      {error && <p className="settings-error" role="alert">{error}</p>}
    </div>
  );
}
